export type LoadingState = "loading" | "loaded" | "failed";
type Subscriber = (state: LoadingState) => void;

const watchOffline = async (loadingPromise: Promise<any>) => {
  return new Promise<void>(async (resolve, reject) => {
    const handleOffline = () => {
      window.removeEventListener("offline", handleOffline);
      reject(new Error("Client is offline during initial loading"));
    };

    window.addEventListener("offline", handleOffline);
    await loadingPromise;
    window.removeEventListener("offline", handleOffline);

    resolve();
  });
};
export class LoadingManager {
  private static _mainPageResourcesLoaded = false;
  private static _mainPageLoadingFailed = false;
  private static _mainPageResourcesLoadedSubscribers = new Set<Subscriber>();
  private static _initialized = false;

  static async init(
    waitFor: Promise<any>[],
    onComplete?: () => void | Promise<void>
  ) {
    if (this._initialized) {
      return;
    }

    this._initialized = true;

    try {
      await watchOffline(Promise.all(waitFor));
      await onComplete?.();
      this.setMainPageResourcesLoaded();
    } catch (error) {
      this.setMainPageLoadingFailed();
      console.error("Error loading resources", error);
    }
  }

  private static updateSubscribers() {
    this._mainPageResourcesLoadedSubscribers.forEach((cb) =>
      cb(this.loadingState)
    );
  }

  static get loadingState() {
    if (this._mainPageLoadingFailed) {
      return "failed";
    }

    return this._mainPageResourcesLoaded ? "loaded" : "loading";
  }

  static get mainPageResourcesLoaded() {
    return this._mainPageResourcesLoaded;
  }

  static get mainPageLoadingFailed() {
    return this._mainPageLoadingFailed;
  }

  private static setMainPageResourcesLoaded() {
    this._mainPageResourcesLoaded = true;
    this.updateSubscribers();
  }

  private static setMainPageLoadingFailed() {
    this._mainPageLoadingFailed = true;
    this.updateSubscribers();
  }

  static subscribeToMainPageResourcesLoaded(cb: Subscriber) {
    cb(this.loadingState);
    this._mainPageResourcesLoadedSubscribers.add(cb);

    return () => {
      this._mainPageResourcesLoadedSubscribers.delete(cb);
    };
  }
}
